import { CompetencyMapSelectedParts } from "helpers/competencyMapSelectParts";
import { formatNumber } from "helpers/index";
import { CompetencyMapInputs } from "../types";
import {
  DeleteSelectedEntitiesResult,
  SelectedEntityIds,
  SelectionCheckboxState,
  SelectionEntity,
  SelectionTree,
  SelectionTreeCompetency,
  SelectionTreeKnowledgeBlock,
} from "./types";

export const EMPTY_SELECTED_ENTITY_IDS: SelectedEntityIds = {
  competencyIds: [],
  knowledgeBockIds: [],
  questionIds: [],
  skillIds: [],
};

const getUniqueIds = (ids: string[]) => Array.from(new Set(ids));

const getSelectedSets = (selected: SelectedEntityIds) => ({
  competencies: new Set(selected.competencyIds),
  knowledgeBlocks: new Set(selected.knowledgeBockIds),
  questions: new Set(selected.questionIds),
  skills: new Set(selected.skillIds),
});

const getSelectedEntityIdsFromSets = (selectedSets: ReturnType<typeof getSelectedSets>): SelectedEntityIds => ({
  competencyIds: Array.from(selectedSets.competencies),
  knowledgeBockIds: Array.from(selectedSets.knowledgeBlocks),
  questionIds: Array.from(selectedSets.questions),
  skillIds: Array.from(selectedSets.skills),
});

const isKnowledgeBlockChecked = (
  knowledgeBlock: SelectionTreeKnowledgeBlock,
  selectedSets: ReturnType<typeof getSelectedSets>
) => {
  if (knowledgeBlock.skills.length === 0) return selectedSets.knowledgeBlocks.has(knowledgeBlock.id);

  return knowledgeBlock.skills.every((skill) => selectedSets.skills.has(skill.id));
};

const isCompetencyChecked = (competency: SelectionTreeCompetency, selectedSets: ReturnType<typeof getSelectedSets>) => {
  if (competency.knowledgeBlocks.length === 0) return selectedSets.competencies.has(competency.id);

  return competency.knowledgeBlocks.every((knowledgeBlock) => isKnowledgeBlockChecked(knowledgeBlock, selectedSets));
};

const syncParentSelection = (selectedSets: ReturnType<typeof getSelectedSets>, tree: SelectionTree) => {
  Object.values(tree.knowledgeBlockMap).forEach((knowledgeBlock) => {
    if (isKnowledgeBlockChecked(knowledgeBlock, selectedSets)) selectedSets.knowledgeBlocks.add(knowledgeBlock.id);
    else selectedSets.knowledgeBlocks.delete(knowledgeBlock.id);
  });

  Object.values(tree.competencyMap).forEach((competency) => {
    if (isCompetencyChecked(competency, selectedSets)) selectedSets.competencies.add(competency.id);
    else selectedSets.competencies.delete(competency.id);
  });
};

export const buildSelectionTree = (
  knowledgeBlocks: CompetencyMapInputs["knowledgeBlocks"] | undefined | null
): SelectionTree => {
  const competencies: SelectionTree["competencies"] = [];
  const competencyMap: SelectionTree["competencyMap"] = {};
  const knowledgeBlockMap: SelectionTree["knowledgeBlockMap"] = {};
  const skillMap: SelectionTree["skillMap"] = {};

  knowledgeBlocks?.forEach((competency) => {
    if (!competency?.id) return;

    const competencyNode: SelectionTreeCompetency = {
      id: competency.id,
      title: competency.title || "",
      knowledgeBlocks: [],
    };

    competency.knowledgeBlockCompetences?.forEach((knowledgeBlock) => {
      if (!knowledgeBlock?.id) return;

      const knowledgeBlockNode: SelectionTreeKnowledgeBlock = {
        id: knowledgeBlock.id,
        title: knowledgeBlock.title || "",
        parentCompetencyId: competency.id!,
        skills: [],
      };

      knowledgeBlock.skills?.forEach((skill) => {
        if (!skill?.id) return;

        const skillNode = {
          id: skill.id,
          title: skill.title,
          confirmedQuestionsCount: skill.confirmedQuestionsCount,
          notConfirmedQuestionsCount: skill.notConfirmedQuestionsCount,
          parentKnowledgeBlockId: knowledgeBlock.id!,
          parentCompetencyId: competency.id!,
        };

        knowledgeBlockNode.skills.push(skillNode);
        skillMap[skill.id] = skillNode;
      });

      knowledgeBlockNode.skills = getUniqueIds(knowledgeBlockNode.skills.map((skill) => skill.id)).map(
        (skillId) => skillMap[skillId]
      );

      competencyNode.knowledgeBlocks.push(knowledgeBlockNode);
      knowledgeBlockMap[knowledgeBlock.id] = knowledgeBlockNode;
    });

    competencyNode.knowledgeBlocks = getUniqueIds(
      competencyNode.knowledgeBlocks.map((knowledgeBlock) => knowledgeBlock.id)
    ).map((knowledgeBlockId) => knowledgeBlockMap[knowledgeBlockId]);

    competencies.push(competencyNode);
    competencyMap[competency.id] = competencyNode;
  });

  return { competencies, competencyMap, knowledgeBlockMap, skillMap };
};

export const getSelectionCheckboxState = (
  entity: SelectionEntity,
  selected: SelectedEntityIds,
  tree: SelectionTree
): SelectionCheckboxState => {
  const selectedSets = getSelectedSets(selected);

  if (entity.type === "skill") {
    return {
      checked: selectedSets.skills.has(entity.id),
      indeterminate: false,
    };
  }

  if (entity.type === "question") {
    return {
      checked: selectedSets.questions.has(entity.id),
      indeterminate: false,
    };
  }

  if (entity.type === "knowledgeBlock") {
    const knowledgeBlock = tree.knowledgeBlockMap[entity.id];
    if (!knowledgeBlock) return { checked: false, indeterminate: false };

    const selectedSkillsCount = knowledgeBlock.skills.filter((skill) => selectedSets.skills.has(skill.id)).length;
    const checked = isKnowledgeBlockChecked(knowledgeBlock, selectedSets);

    return {
      checked,
      indeterminate: !checked && selectedSkillsCount > 0,
    };
  }

  const competency = tree.competencyMap[entity.id];
  if (!competency) return { checked: false, indeterminate: false };

  const checked = isCompetencyChecked(competency, selectedSets);
  const hasPartiallySelectedKnowledgeBlocks = competency.knowledgeBlocks.some((knowledgeBlock) => {
    const knowledgeBlockState = getSelectionCheckboxState(
      { type: "knowledgeBlock", id: knowledgeBlock.id },
      selected,
      tree
    );

    return knowledgeBlockState.checked || knowledgeBlockState.indeterminate;
  });

  return {
    checked,
    indeterminate: !checked && hasPartiallySelectedKnowledgeBlocks,
  };
};

export const toggleSelectionEntity = (
  selected: SelectedEntityIds,
  tree: SelectionTree,
  entity: SelectionEntity
): SelectedEntityIds => {
  const selectedSets = getSelectedSets(selected);
  const { checked, indeterminate } = getSelectionCheckboxState(entity, selected, tree);
  const shouldClear = checked || indeterminate;

  if (entity.type === "competency") {
    const competency = tree.competencyMap[entity.id];
    if (!competency) return selected;

    if (shouldClear) {
      selectedSets.competencies.delete(competency.id);
      competency.knowledgeBlocks.forEach((knowledgeBlock) => {
        selectedSets.knowledgeBlocks.delete(knowledgeBlock.id);
        knowledgeBlock.skills.forEach((skill) => selectedSets.skills.delete(skill.id));
      });
    } else {
      selectedSets.competencies.add(competency.id);
      competency.knowledgeBlocks.forEach((knowledgeBlock) => {
        selectedSets.knowledgeBlocks.add(knowledgeBlock.id);
        knowledgeBlock.skills.forEach((skill) => selectedSets.skills.add(skill.id));
      });
    }
  }

  if (entity.type === "knowledgeBlock") {
    const knowledgeBlock = tree.knowledgeBlockMap[entity.id];
    if (!knowledgeBlock) return selected;

    if (shouldClear) {
      selectedSets.knowledgeBlocks.delete(knowledgeBlock.id);
      knowledgeBlock.skills.forEach((skill) => selectedSets.skills.delete(skill.id));
    } else {
      selectedSets.knowledgeBlocks.add(knowledgeBlock.id);
      knowledgeBlock.skills.forEach((skill) => selectedSets.skills.add(skill.id));
    }
  }

  if (entity.type === "skill") {
    if (shouldClear) selectedSets.skills.delete(entity.id);
    else selectedSets.skills.add(entity.id);
  }

  if (entity.type === "question") {
    if (shouldClear) selectedSets.questions.delete(entity.id);
    else selectedSets.questions.add(entity.id);
  }

  syncParentSelection(selectedSets, tree);

  return getSelectedEntityIdsFromSets(selectedSets);
};

export const normalizeSelectedEntityIds = (selected: SelectedEntityIds, tree: SelectionTree): SelectedEntityIds => {
  const normalizedSelection: SelectedEntityIds = {
    competencyIds: selected.competencyIds.filter((id) => Boolean(tree.competencyMap[id])),
    knowledgeBockIds: selected.knowledgeBockIds.filter((id) => Boolean(tree.knowledgeBlockMap[id])),
    questionIds: selected.questionIds,
    skillIds: selected.skillIds.filter((id) => Boolean(tree.skillMap[id])),
  };

  const selectedSets = getSelectedSets(normalizedSelection);
  syncParentSelection(selectedSets, tree);

  return getSelectedEntityIdsFromSets(selectedSets);
};

export const hasSelectedEntities = (selected: SelectedEntityIds) =>
  selected.competencyIds.length > 0 ||
  selected.knowledgeBockIds.length > 0 ||
  selected.questionIds.length > 0 ||
  selected.skillIds.length > 0;

export const getSelectedEntityCountsLabel = (selected: SelectedEntityIds) => {
  const items = [
    selected.competencyIds.length
      ? formatNumber(["компетенция", "компетенции", "компетенций"], selected.competencyIds.length)
      : null,
    selected.knowledgeBockIds.length
      ? formatNumber(["блок знаний", "блока знаний", "блоков знаний"], selected.knowledgeBockIds.length)
      : null,
    selected.questionIds.length ? formatNumber(["вопрос", "вопроса", "вопросов"], selected.questionIds.length) : null,
    selected.skillIds.length ? formatNumber(["навык", "навыка", "навыков"], selected.skillIds.length) : null,
  ].filter(Boolean);

  return items.join(" · ");
};

export const getSelectedEntityDeleteDescription = (selected: SelectedEntityIds) => {
  const label = getSelectedEntityCountsLabel(selected);
  return label ? `Будут удалены: ${label}.` : "";
};

export const getSelectedParts = (selected: SelectedEntityIds): CompetencyMapSelectedParts | undefined => {
  if (!hasSelectedEntities(selected)) return undefined;

  return {
    competencyIds: selected.competencyIds.length ? selected.competencyIds : undefined,
    knowledgeBockIds: selected.knowledgeBockIds.length ? selected.knowledgeBockIds : undefined,
    questionIds: selected.questionIds.length ? selected.questionIds : undefined,
    skillIds: selected.skillIds.length ? selected.skillIds : undefined,
  };
};

export const hasSelectedQuestions = (selected: SelectedEntityIds, tree: SelectionTree) =>
  selected.skillIds.some((skillId) => {
    const skill = tree.skillMap[skillId];
    if (!skill) return false;

    return Boolean((skill.confirmedQuestionsCount || 0) + (skill.notConfirmedQuestionsCount || 0));
  });

export const removeSelectedEntitiesFromKnowledgeBlocks = (
  knowledgeBlocks: CompetencyMapInputs["knowledgeBlocks"],
  selected: SelectedEntityIds
): DeleteSelectedEntitiesResult => {
  const competencyIdsSet = new Set(selected.competencyIds);
  const knowledgeBlockIdsSet = new Set(selected.knowledgeBockIds);
  const skillIdsSet = new Set(selected.skillIds);
  const deletedSkillIds = new Set<string>();

  const nextKnowledgeBlocks =
    knowledgeBlocks?.reduce<NonNullable<CompetencyMapInputs["knowledgeBlocks"]>>((competenciesAcc, competency) => {
      if (!competency) return competenciesAcc;

      if (competency.id && competencyIdsSet.has(competency.id)) {
        competency.knowledgeBlockCompetences?.forEach((knowledgeBlock) =>
          knowledgeBlock.skills?.forEach((skill) => {
            if (skill.id) deletedSkillIds.add(skill.id);
          })
        );

        return competenciesAcc;
      }

      const nextKnowledgeBlockCompetences =
        competency.knowledgeBlockCompetences?.reduce<NonNullable<typeof competency.knowledgeBlockCompetences>>(
          (knowledgeBlocksAcc, knowledgeBlock) => {
            if (!knowledgeBlock) return knowledgeBlocksAcc;

            if (knowledgeBlock.id && knowledgeBlockIdsSet.has(knowledgeBlock.id)) {
              knowledgeBlock.skills?.forEach((skill) => {
                if (skill.id) deletedSkillIds.add(skill.id);
              });

              return knowledgeBlocksAcc;
            }

            const nextSkills =
              knowledgeBlock.skills?.filter((skill) => {
                if (!skill?.id || !skillIdsSet.has(skill.id)) return true;

                deletedSkillIds.add(skill.id);
                return false;
              }) || [];

            knowledgeBlocksAcc.push({
              ...knowledgeBlock,
              skills: nextSkills,
            });

            return knowledgeBlocksAcc;
          },
          []
        ) || [];

      competenciesAcc.push({
        ...competency,
        knowledgeBlockCompetences: nextKnowledgeBlockCompetences,
      });

      return competenciesAcc;
    }, []) || [];

  return {
    knowledgeBlocks: nextKnowledgeBlocks,
    deletedSkillIds: Array.from(deletedSkillIds),
  };
};
